import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Close,
  Download,
  TableChart,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment, { Moment } from "moment";
import { useState } from "react";
import { hostname } from "../../../utils";

type ExportFormat = "csv" | "xls";
type BreakdownType = "organizations" | "companies" | "users";

interface GenerateReportDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  whitelabelId: string;
  companyProvisioningEnabled: boolean;
}

interface OrganizationBreakdownItem {
  organization_id: string | null;
  organization_name: string;
  credits_usage: number;
  audience_records_usage: number;
  companies_usage: number;
  users_usage: number;
}

interface CompanyBreakdownItem {
  company_id: string;
  company_name: string;
  organization_id: string | null;
  organization_name: string;
  credits_usage: number;
  audience_records_usage: number;
  users_usage: number;
}

interface UserBreakdownItem {
  user_id: string;
  email: string;
  display_name: string | null;
  company_id: string | null;
  company_name: string;
  organization_id: string | null;
  organization_name: string;
  credits_usage: number;
  audience_records_usage: number;
  requests: number;
}

type BreakdownItem = OrganizationBreakdownItem | CompanyBreakdownItem | UserBreakdownItem;

interface UsageBreakdownResponse {
  range: {
    startDate: string;
    endDate: string;
  };
  breakdown: BreakdownType;
  items: BreakdownItem[];
}

interface ColumnDef {
  key: string;
  label: string;
}

const columnsByBreakdown: Record<BreakdownType, ColumnDef[]> = {
  organizations: [
    { key: "organization_name", label: "Organization" },
    { key: "credits_usage", label: "Credits Usage" },
    { key: "audience_records_usage", label: "Audience Records" },
    { key: "companies_usage", label: "Company Usage" },
    { key: "users_usage", label: "User Usage" },
  ],
  companies: [
    { key: "company_name", label: "Company" },
    { key: "organization_name", label: "Organization" },
    { key: "credits_usage", label: "Credits Usage" },
    { key: "audience_records_usage", label: "Audience Records" },
    { key: "users_usage", label: "User Usage" },
  ],
  users: [
    { key: "display_name", label: "User" },
    { key: "email", label: "Email" },
    { key: "company_name", label: "Company" },
    { key: "organization_name", label: "Organization" },
    { key: "credits_usage", label: "Credits Usage" },
    { key: "audience_records_usage", label: "Audience Records" },
    { key: "requests", label: "Requests" },
  ],
};

const getCellValue = (item: BreakdownItem, key: string): string => {
  const value = (item as unknown as Record<string, unknown>)[key];
  if (value == null) {
    return "";
  }
  if (typeof value === "number") {
    return value.toLocaleString();
  }
  return String(value);
};

const escapeCSV = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const generateCSV = (items: BreakdownItem[], columns: ColumnDef[]): string => {
  const headers = columns.map((c) => escapeCSV(c.label)).join(",");
  const rows = items.map((item) => columns.map((c) => escapeCSV(getCellValue(item, c.key))).join(","));
  return [headers, ...rows].join("\n");
};

const generateXLS = (items: BreakdownItem[], columns: ColumnDef[]): string => {
  let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Usage Report</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table border="1">`;

  html += "<tr>";
  columns.forEach((column) => {
    html += `<th style="background-color:#f0f0f0;font-weight:bold;">${column.label}</th>`;
  });
  html += "</tr>";

  items.forEach((item) => {
    html += "<tr>";
    columns.forEach((column) => {
      const value = getCellValue(item, column.key).replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html += `<td>${value}</td>`;
    });
    html += "</tr>";
  });

  html += "</table></body></html>";
  return html;
};

export default function GenerateReportDialog({
  open,
  onClose,
  getToken,
  whitelabelId,
  companyProvisioningEnabled,
}: GenerateReportDialogProps) {
  void companyProvisioningEnabled;

  const [exportFormat, setExportFormat] = useState<ExportFormat>("csv");
  const [breakdownType, setBreakdownType] = useState<BreakdownType>("organizations");
  const [startDate, setStartDate] = useState<Moment | null>(moment().subtract(30, "days"));
  const [endDate, setEndDate] = useState<Moment | null>(moment());
  const [data, setData] = useState<UsageBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBreakdown = async (targetBreakdown: BreakdownType): Promise<UsageBreakdownResponse | null> => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return null;
    }

    if (endDate.isBefore(startDate, "day")) {
      setError("End date must be on or after the start date.");
      return null;
    }

    try {
      const token = await getToken();
      const response = await axios.get<UsageBreakdownResponse>(
        `${hostname}/whitelabel/${whitelabelId}/usage-breakdown`,
        {
          params: {
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
            breakdown: targetBreakdown,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setData(response.data);
      return response.data;
    } catch (err) {
      console.error("Error loading usage breakdown:", err);
      setError("Failed to load usage data. Please try again.");
      return null;
    }
  };

  const handleLoadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchBreakdown(breakdownType);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = data && data.breakdown === breakdownType ? data : await fetchBreakdown(breakdownType);
      if (!response) {
        return;
      }

      const columns = columnsByBreakdown[breakdownType];
      let content: string;
      let filename: string;
      let mimeType: string;

      const timestamp = moment().format("YYYY-MM-DD");
      const rangeLabel = `${response.range.startDate}_to_${response.range.endDate}`;

      if (exportFormat === "csv") {
        content = generateCSV(response.items, columns);
        filename = `whitelabel_${breakdownType}_usage_${rangeLabel}_${timestamp}.csv`;
        mimeType = "text/csv;charset=utf-8;";
      } else {
        content = generateXLS(response.items, columns);
        filename = `whitelabel_${breakdownType}_usage_${rangeLabel}_${timestamp}.xls`;
        mimeType = "application/vnd.ms-excel;charset=utf-8;";
      }

      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting usage report:", err);
      setError("Failed to export report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const currentColumns = columnsByBreakdown[breakdownType];
  const rows = data?.breakdown === breakdownType ? data.items : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TableChart />
            Generate Usage Report
          </Box>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(value: any) => {
                  setStartDate(value);
                  setData(null);
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(value: any) => {
                  setEndDate(value);
                  setData(null);
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>

          <FormControl component="fieldset">
            <FormLabel component="legend">Breakdown</FormLabel>
            <RadioGroup
              row
              value={breakdownType}
              onChange={(e) => {
                setBreakdownType(e.target.value as BreakdownType);
                setData(null);
              }}
            >
              <FormControlLabel value="organizations" control={<Radio />} label="Organizations" />
              <FormControlLabel value="companies" control={<Radio />} label="Companies" />
              <FormControlLabel value="users" control={<Radio />} label="Users" />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset">
            <FormLabel component="legend">Export Format</FormLabel>
            <RadioGroup
              row
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
            >
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
              <FormControlLabel value="xls" control={<Radio />} label="Excel (XLS)" />
            </RadioGroup>
          </FormControl>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={handleLoadData} disabled={loading}>
              {loading ? "Loading..." : "Load Usage Data"}
            </Button>
            <Button variant="contained" onClick={handleExport} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <Download />}>
              {loading ? "Exporting..." : "Export Report"}
            </Button>
          </Box>

          {data && data.breakdown === breakdownType && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Showing {breakdownType} usage from {data.range.startDate} to {data.range.endDate}
              </Typography>

              <TableContainer sx={{ maxHeight: 360, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {currentColumns.map((column) => (
                        <TableCell key={column.key}>{column.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((item, index) => (
                      <TableRow key={`${breakdownType}-${index}`}>
                        {currentColumns.map((column) => (
                          <TableCell key={`${breakdownType}-${index}-${column.key}`}>
                            {getCellValue(item, column.key)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={currentColumns.length}>
                          <Typography variant="body2" color="text.secondary">
                            No usage found for this date range.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
